import { Fragment, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useSideNav } from 'hooks';
import { routes } from 'constants/index';

import Logo from '../Logo';

import { NavItem } from '../Header/navigation';

import './side-nav.scss';

interface SideNavProps {
  items: NavItem[];
  // Below 768px the sidebar is off-canvas and opens as a drawer from the
  // header, because there is no room to keep it alongside the content.
  isDrawerOpen?: boolean;
  onDrawerClose?: () => void;
}

const pathOf = (to: string) => to.split('?')[0];

// The path or anything beneath it, on a segment boundary, so /list-one does
// not claim /list-one-archive.
function isWithin(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/*
 * The one submenu the current page belongs to. Siblings can nest, as Details
 * (/list-one) and Summary (/list-one/statuses) do, and NavLink alone would
 * light both, so the most specific match wins.
 */
function activeSubmenuKey(pathname: string, submenus: NavItem['submenus']) {
  return submenus
    .filter((submenu) => isWithin(pathname, pathOf(submenu.to)))
    .sort((a, b) => pathOf(b.to).length - pathOf(a.to).length)[0]?.key;
}

function SideNavGroup({
  item,
  isCollapsed,
}: {
  item: NavItem;
  isCollapsed: boolean;
}) {
  const { pathname } = useLocation();

  // A group holding the page you are on opens itself. Without this the docs
  // sidebar shows three collapsed products and no sign of where you are.
  const activeKey = activeSubmenuKey(pathname, item.submenus);
  const holdsCurrentPage = !!activeKey;

  const [isExpanded, setExpanded] = useState(holdsCurrentPage);

  useEffect(() => {
    if (holdsCurrentPage) setExpanded(true);
  }, [holdsCurrentPage]);

  if (item.submenus.length === 0) {
    return (
      <NavLink
        className="side-nav__link"
        to={item.to || '/'}
        title={isCollapsed ? item.label : undefined}
      >
        <i className={`side-nav__icon pi ${item.icon}`} aria-hidden="true" />
        {!isCollapsed && <span className="side-nav__label">{item.label}</span>}
      </NavLink>
    );
  }

  // Collapsed, a parent has no room to expand inline, so it links to its first
  // child rather than becoming a dead icon.
  if (isCollapsed) {
    return (
      <NavLink
        className="side-nav__link"
        to={item.submenus[0].to}
        title={item.label}
      >
        <i className={`side-nav__icon pi ${item.icon}`} aria-hidden="true" />
      </NavLink>
    );
  }

  return (
    <div className="side-nav__group">
      <button
        type="button"
        className="side-nav__link side-nav__link--parent"
        aria-expanded={isExpanded}
        onClick={() => setExpanded((expanded) => !expanded)}
      >
        <i className={`side-nav__icon pi ${item.icon}`} aria-hidden="true" />
        <span className="side-nav__label">{item.label}</span>
        <i
          className={classNames('side-nav__chevron pi', {
            'pi-chevron-down': isExpanded,
            'pi-chevron-right': !isExpanded,
          })}
          aria-hidden="true"
        />
      </button>

      {isExpanded && (
        <div className="side-nav__submenu">
          {item.submenus.map((submenu) => {
            const isActive = submenu.key === activeKey;

            return (
              <Link
                key={submenu.key}
                className={classNames('side-nav__sublink', {
                  active: isActive,
                })}
                aria-current={isActive ? 'page' : undefined}
                to={submenu.to}
              >
                {submenu.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/*
 * Vertical navigation, on trial against the horizontal bar.
 *
 * Collapses to an icon rail because the tables here are wide, List 2 and
 * List 3 in particular, and a permanent 22rem of chrome costs columns that
 * people are reading. Collapsed, it gives all but 6rem of that back.
 */
export default function SideNav({
  items,
  isDrawerOpen = false,
  onDrawerClose,
}: SideNavProps) {
  const { isCollapsed, toggleCollapsed } = useSideNav();
  const { pathname } = useLocation();

  // Navigating closes the drawer; on a phone the destination is behind it.
  useEffect(() => {
    if (isDrawerOpen) onDrawerClose?.();
    // Only the route change should close it, not a new callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (!isDrawerOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') onDrawerClose?.();
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDrawerOpen, onDrawerClose]);

  return (
    <>
      {isDrawerOpen && (
        <div
          className="side-nav__scrim"
          role="presentation"
          onClick={onDrawerClose}
        />
      )}

      <nav
        className={classNames('side-nav', {
          'side-nav--collapsed': isCollapsed,
          'side-nav--drawer-open': isDrawerOpen,
        })}
        aria-label="Sections"
      >
        {/* The logo takes the whole top row; collapsed, the diamond stands in
            for it, so the brand never leaves the rail. */}
        <div className="side-nav__brand">
          <NavLink
            to={routes.main}
            className="side-nav__brand-link"
            aria-label="Home"
          >
            <Logo
              className={classNames('side-nav__brand-logo', {
                'side-nav__brand-logo--mark': isCollapsed,
              })}
              variant={isCollapsed ? 'mark' : 'full'}
            />
          </NavLink>
        </div>

        <div className="side-nav__items">
          {items.map((item, index) => {
            const startsSection =
              !!item.section && item.section !== items[index - 1]?.section;

            return (
              <Fragment key={item.key}>
                {startsSection &&
                  (isCollapsed ? (
                    <hr className="side-nav__divider" />
                  ) : (
                    <div className="side-nav__section">{item.section}</div>
                  ))}
                <SideNavGroup item={item} isCollapsed={isCollapsed} />
              </Fragment>
            );
          })}
        </div>

        {/* A rare, set-once choice, so it waits at the foot of the rail. It
            keeps a nav row's geometry, so it reads as part of the list. */}
        <div className="side-nav__footer">
          <button
            type="button"
            className="side-nav__collapse"
            onClick={toggleCollapsed}
            aria-label={
              isCollapsed ? 'Expand navigation' : 'Collapse navigation'
            }
            title={isCollapsed ? 'Expand navigation' : undefined}
          >
            <i
              className={classNames('side-nav__icon pi', {
                'pi-angle-double-right': isCollapsed,
                'pi-angle-double-left': !isCollapsed,
              })}
              aria-hidden="true"
            />
            {!isCollapsed && <span>Collapse</span>}
          </button>
        </div>
      </nav>
    </>
  );
}
